import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import cytoscape from 'cytoscape';
import { CytoscapeOptions } from 'cytoscape';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { CimSubstationService } from '../../../services/cim-substation.service';
import { SvgDesignService } from '../../../services/svg-design.service';
import { SchematicLegendItem } from '../../../models/schematic.model';

enum AcLineLegendType {
  AcLine = 'AcLine',
  Incoming = 'IncomingLines',
  Outgoing = 'OutgoingLines'
}

interface ApiSchematicNode {
  id: string;
  label: string;
  type: string;
  symbol: string;
  color: string;
}

interface ApiSchematicEdge {
  id: string;
  source: string;
  destination: string;
  label: string;
  voltageClass: 'HV' | 'MV' | 'LV';
  acLineDirection: string;
}

interface ApiSchematic {
  nodes: ApiSchematicNode[];
  edges: ApiSchematicEdge[];
}

interface BusbarStructure {
  start: string;
  end: string;
  connectionNodes: string[];
}

@Component({
  selector: 'lib-cim-substation-equipment-schematic',
  standalone: false,
  templateUrl: './cim-substation-equipment-schematic.component.html',
  styleUrl: './cim-substation-equipment-schematic.component.scss'
})
export class CimSubstationEquipmentSchematicComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() templateInstanceId: number | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() legendDataChange = new EventEmitter<SchematicLegendItem[]>();

  @ViewChild('cyContainer', { static: true })
  cyContainer!: ElementRef<HTMLDivElement>;
  private cy!: cytoscape.Core;
  private structuralMap: Record<string, BusbarStructure> = {};
  dataFromApi!: ApiSchematic;
  private resizeObserver!: ResizeObserver;

  constructor(private toastr: ToastrService, private svgDesignService: SvgDesignService, private cimSubstationService: CimSubstationService){}

  ngAfterViewInit(): void {
    this.getEquipmentSchemaById();
  }

  getEquipmentSchemaById() {
    if (!this.templateInstanceId) {
      this.toastr.warning('Schematic is not available for this asset.');
      return;
    }

    forkJoin({
      schema: this.cimSubstationService.getEquipmentSchema(this.templateInstanceId),
      layout: this.cimSubstationService.getSchematicLayoutJsonById(this.templateInstanceId)
    }).subscribe({
      next: ({ schema, layout }) => {

        const schemaData = schema.body;
        const layoutData = layout.body;

        if (schemaData?.status !== 200 && schemaData?.status !== 'OK') {
          this.toastr.error(schemaData?.message);
          return;
        }

        this.dataFromApi = schemaData.data;

        let savedLayout: any[] | null = null;

        if (layoutData?.status === 200 || layoutData?.status === 'OK') {
          if (layoutData.data) {
            savedLayout = JSON.parse(layoutData.data);
          }
        }

        this.generateSchematicDiagram(this.dataFromApi, savedLayout);
      },
      error: () => {
        this.toastr.error('Error fetching schematic data.');
      }
    });
  }

  generateSchematicDiagram(data: ApiSchematic, savedLayout?: any[] | null){
    const cyJson = this.createDiagramJson(data);

    try
    {
      this.cy = cytoscape(cyJson);
    }
    catch
    {
      this.toastr.error("Something went wrong with generating diagram.")
      return;
    }

    // just in case of rare fail cases
    if (!this.cy){
      this.toastr.error("Something went wrong with generating diagram.")
      return;
    }

    this.setLegendData(this.dataFromApi);

    this.cy.ready(() => {
      this.cy.elements().forEach(ele => {
        ele.style('background-image', ele.style('background-image'));
      });
    });

    this.cy.ready(() => {
      if (savedLayout && savedLayout.length > 0) {
        this.applySavedLayout(savedLayout);
      } else {
        this.applyCustomVerticalLayout();
      }

      // Small timeout ensures browser finished rendering positions
      setTimeout(() => {
        this.cy.fit(undefined, 100);
        this.cy.center();
      }, 0);
    });

    this.resizeObserver = new ResizeObserver(() => {
      if (!this.cy) return;

      this.cy.resize();
      this.cy.fit(undefined, 40);
    });

    this.resizeObserver.observe(this.cyContainer.nativeElement);

    this.createTooltips();
    this.unifiedDragBehavior();
  }

  private createDiagramJson(data: ApiSchematic) : CytoscapeOptions {
    const structuralTypes = ['Busbar'];

    const elements: any[] = [];
    this.structuralMap = {};

    // First pass – create nodes
    data.nodes.forEach(n => {
      const isStructural = structuralTypes.includes(n.type);

      if (isStructural) {

        const startId = `${n.id}-start`;
        const endId = `${n.id}-end`;

        // Find all edges connected to this busbar
        const relatedEdges = data.edges.filter(e =>
          e.source === n.id || e.destination === n.id
        );

        const connectionNodeIds: string[] = [];

        relatedEdges.forEach((edge, index) => {
          const connId = `${n.id}-conn-${index}`;
          connectionNodeIds.push(connId);

          elements.push({
            data: { id: connId },
            classes: 'structural-node'
          });
        });

        this.structuralMap[n.id] = {
          start: startId,
          end: endId,
          connectionNodes: connectionNodeIds
        };

        // Start node
        elements.push({
          data: { id: startId },
          classes: 'structural-node'
        });

        // End node
        elements.push({
          data: { id: endId },
          classes: 'structural-node'
        });

        // Internal red edge
        elements.push({
          data: {
            id: `${n.id}-internal`,
            source: startId,
            target: endId,
            label: n.label
          },
          classes: 'structural-edge'
        });
      } else {
        const icon = this.svgDesignService.getSvgDesign(
          n.symbol ?? "",
          n.color ?? ""
        );

        elements.push({
          data: {
            id: n.id,
            label: n.label
          },
          classes: 'equipment-node',
          style: {
            'background-image': icon?.svg
              ? `data:image/svg+xml;base64,${btoa(
                  String.fromCharCode(...new TextEncoder().encode(icon.svg))
                )}`
              : '',
            'background-fit': 'none',
            'background-width': 36,
            'background-height': 36,
            'background-position-x': '50%',
            'background-position-y': '50%',
            'background-repeat': 'no-repeat',
          },
          wheelSensitivity: 0.4
        });
      }
    });

    // Second pass – create edges
    data.edges.forEach(e => {

      let source = e.source;
      let target = e.destination;

      // handle null source
      if (!source) {
        source = `null-source-${e.id}`;

        elements.push({
          data: { id: source },
          classes: 'null-node'
        });
      }

      // handle null target
      if (!target) {
        target = `null-target-${e.id}`;

        elements.push({
          data: { id: target },
          classes: 'null-node'
        });
      }

      const sourceStructural = this.structuralMap[source];
      const targetStructural = this.structuralMap[target];

      // Handle structural source
      if (sourceStructural) {
        const relatedEdges = data.edges.filter(edge =>
          edge.source === e.source || edge.destination === e.source
        );

        const index = relatedEdges.findIndex(edge => edge.id === e.id);
        source = sourceStructural.connectionNodes[index];
      }

      // Handle structural target
      if (targetStructural) {
        const relatedEdges = data.edges.filter(edge =>
          edge.source === e.destination || edge.destination === e.destination
        );

        const index = relatedEdges.findIndex(edge => edge.id === e.id);
        target = targetStructural.connectionNodes[index];
      }

      const edgeClass =
        e.acLineDirection === 'Outgoing'
          ? 'outgoing-edge'
          : e.acLineDirection === 'Incoming'
            ? 'incoming-edge'
            : 'normal-edge';

      elements.push({
        data: {
          id: e.id,
          source,
          target,
          label: e.label,
          voltageClass: e.voltageClass
        },
        classes: edgeClass
      });
    });

    return {
      container: this.cyContainer.nativeElement,
      elements,
      wheelSensitivity: 0.5,
      // minZoom: 0.2,
      // maxZoom: 3,
      boxSelectionEnabled: false,
      style: [
        {
          selector: '.equipment-node',
          style: {
            width: 60,
            height: 60,
            shape: 'ellipse',
            'background-color': '#3b3b3b',
            'border-width': 0
          }
        },
        // Transparent structural nodes
        {
          selector: '.structural-node',
          style: {
            width: 30,
            height: 3,
            'background-opacity': 0,
            'border-width': 0
          }
        },
        {
          selector: '.null-node',
          style: {
            width: 20,
            height: 20,
            shape: 'ellipse',
            'background-color': '#000000',
            'background-opacity': 0,
            'border-width': 0
          }
        },
        {
          selector: '.normal-edge',
          style: {
            width: 3,
            'line-color': '#7a49ca',
            'curve-style': 'straight',
            'target-arrow-shape': 'none'
          }
        },
        {
          selector: '.structural-edge',
          style: {
            width: 3,
            'line-color': '#ff0000',
            'curve-style': 'straight',
            'target-arrow-shape': 'none'
          }
        },
        {
          selector: '.incoming-edge',
          style: {
            width: 3,
            'line-color': '#94d02c',
            'curve-style': 'straight',
            'target-arrow-shape': 'none'
          }
        },
        {
          selector: '.outgoing-edge',
          style: {
            width: 3,
            'line-color': '#ffc800',
            'curve-style': 'straight',
            'target-arrow-shape': 'none'
          }
        }
      ],
      layout: {
        name: 'preset'
      }
    };
  }

  onPublish(){
    const obj = {
      substationTemplateInstanceId: this.templateInstanceId,
      schematicLayoutJson: JSON.stringify(this.getMinimalCytoscapeNodes(this.cy!)),
    }

    this.cimSubstationService.saveSchematicLayoutJson(obj).subscribe({
      next: (response: any) => {
        if (response?.body?.status == 200 || response?.body?.status == 'OK') {
          this.toastr.success('Schematic published successfully.');
        } else{
          this.toastr.error((response?.body?.message));
        }
      }
    })
  }
  
  getMinimalCytoscapeNodes(cy: cytoscape.Core) {
    return cy.nodes().map(node => {
      const json = node.json();
      return {
        id: json['data'].id,
        position: json['position']
      };
    });
  }

  private createTooltips(){
    const tooltip = document.createElement('div');
    tooltip.style.position = 'absolute';
    tooltip.style.pointerEvents = 'none';
    tooltip.style.color = "#ffffff"
    tooltip.style.background = '#1a1a1a';
    tooltip.style.border = '1px solid #303030';
    tooltip.style.padding = '4px 8px';
    tooltip.style.borderRadius = '4px';
    tooltip.style.fontSize = '12px';
    tooltip.style.display = 'none';
    tooltip.style.zIndex = '9999';
    tooltip.style.display = 'block';
    tooltip.style.visibility = 'hidden';

    this.cy!.container()!.appendChild(tooltip);

    let tooltipActive = false;

    // so transparent nodes do not have a blank tooltip
    this.cy!.on('mouseover', 'node.equipment-node', (e) => {
      tooltip.textContent = e.target.data('label');
      tooltipActive = true;
    });

    this.cy!.on('mouseover', 'edge', (e) => {

      const label = e.target.data('label');

      if (!label) {
        tooltipActive = false;
        tooltip.style.visibility = 'hidden';
        return;
      }

      tooltip.textContent = label;
      tooltipActive = true;
    });

    this.cy!.on('mousemove', (e) => {
      if (!tooltipActive) return;

      const zoom = parseFloat(getComputedStyle(document.documentElement).zoom || '1');

      const offsetX = e.originalEvent.offsetX / zoom;
      const offsetY = e.originalEvent.offsetY / zoom;

      tooltip.style.left = `${offsetX + 15}px`;
      tooltip.style.top = `${offsetY - 2}px`;

      tooltip.style.visibility = 'visible';
    });

    this.cy!.on('mouseout', () => {
      tooltipActive = false;
      tooltip.style.visibility = 'hidden';
    });

    this.cy!.on('grab', () => {
      tooltipActive = false;
      tooltip.style.visibility = 'hidden';
    });

    this.cy!.on('tap', () => {
      tooltipActive = false;
      tooltip.style.visibility = 'hidden';
    });
  }

  private unifiedDragBehavior() {

    Object.values(this.structuralMap).forEach(busbar => {

      const nodes = [
        this.cy.getElementById(busbar.start),
        this.cy.getElementById(busbar.end),
        ...busbar.connectionNodes.map(id => this.cy.getElementById(id))
      ];

      let lastPositions: Record<string, cytoscape.Position> = {};

      nodes.forEach(n => n.grabify());

      nodes.forEach(node => {

        let dragAxis: 'x' | 'y' | null = null;

        node.on('grab', () => {
          nodes.forEach(n => {
            lastPositions[n.id()] = { ...n.position() };
          });

          dragAxis = null;
        });

        node.on('drag', () => {

          const currentPos = node.position();
          const lastPos = lastPositions[node.id()];

          const dx = currentPos.x - lastPos.x;
          const dy = currentPos.y - lastPos.y;

          if (!dragAxis) {
            dragAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          }

          // Vertical movement → move entire busbar
          if (dragAxis === 'y') {

            nodes.forEach(n => {
              if (n.id() !== node.id()) {
                const pos = n.position();
                n.position({
                  x: pos.x,
                  y: pos.y + dy
                });
              }
            });
          }

          // Horizontal movement → move ONLY selected node
          if (dragAxis === 'x') {

            node.position({
              x: currentPos.x,
              y: lastPos.y
            });
          }

          nodes.forEach(n => {
            lastPositions[n.id()] = { ...n.position() };
          });
        });
      });

      // Structural Busbar drag movement (tapping on the edges that make the busbar)
      const edgeId = `${busbar.start.replace('-start','')}-internal`;
      const edge = this.cy.getElementById(edgeId);

      if (edge.nonempty()) {

        let isDragging = false;
        let lastMousePos: { x: number; y: number };

        // Start drag
        this.cy.on('tapstart', `edge#${edgeId}`, (event) => {

          isDragging = true;

          this.cy.userPanningEnabled(false);
          this.cy.boxSelectionEnabled(false);

          lastMousePos = event.position;
        });

        // Track mouse movement
        this.cy.on('mousemove', (event) => {

          if (!isDragging) return;

          const current = event.position;

          const dx = current.x - lastMousePos.x;
          const dy = current.y - lastMousePos.y;

          nodes.forEach(n => {
            const pos = n.position();

            n.position({
              x: pos.x + dx,
              y: pos.y + dy
            });
          });

          lastMousePos = current;
        });

        // Stop dragging
        this.cy.on('tapend', () => {
          if (!isDragging) return;

          isDragging = false;
          this.cy.userPanningEnabled(true);
        });
      }

    });
  }

  private applyCustomVerticalLayout() {

    const verticalSpacing = 220;
    const horizontalSpacing = 180;

    const baseSegmentWidth = 140; // width between transparent nodes
    const padding = 120;          // left + right padding

    const centerX = 600;
    let currentY = 150;

    // Place all busbars first (top layer)
    const busbars = this.dataFromApi.nodes.filter(n => n.type === 'Busbar');

    busbars.forEach(busbar => {

      const structural = this.structuralMap[busbar.id];
      if (!structural) return;

      const startNode = this.cy.getElementById(structural.start);
      const endNode = this.cy.getElementById(structural.end);
      const connectionNodes = structural.connectionNodes.map(id =>
        this.cy.getElementById(id)
      );

      // Calculate width per busbar
      const totalSegments = connectionNodes.length + 1;
      const busbarLength = totalSegments * baseSegmentWidth + padding;
      const segmentWidth = busbarLength / totalSegments;

      // Place start
      startNode.position({
        x: centerX - busbarLength / 2,
        y: currentY
      });

      // Place connection nodes equally spaced
      connectionNodes.forEach((node, index) => {
        node.position({
          x: centerX - busbarLength / 2 + segmentWidth * (index + 1),
          y: currentY
        });
      });

      // Place end
      endNode.position({
        x: centerX + busbarLength / 2,
        y: currentY
      });

      // Move Y for next busbar (if multiple exist)
      currentY += verticalSpacing;
    });

    // Place all non-structural equipment below last busbar
    const equipment = this.dataFromApi.nodes
      .filter(n => !this.structuralMap[n.id]);

    let equipmentX = centerX - (equipment.length - 1) * horizontalSpacing / 2;

    equipment.forEach(node => {

      const cyNode = this.cy.getElementById(node.id);

      if (cyNode.nonempty()) {
        cyNode.position({
          x: equipmentX,
          y: currentY + verticalSpacing
        });

        equipmentX += horizontalSpacing;
      }
    });
  }

  private applySavedLayout(savedLayout: any[]) {

    const savedMap = new Map<string, cytoscape.Position>();

    savedLayout.forEach(nodeData => {
      savedMap.set(nodeData.id, nodeData.position);
    });

    const brokenBusbars: string[] = [];

    // First pass: detect broken busbars
    Object.entries(this.structuralMap).forEach(([busbarId, structure]) => {

      const allNodeIds = [
        structure.start,
        structure.end,
        ...structure.connectionNodes
      ];

      const isBroken = allNodeIds.some(id => {
        const pos = savedMap.get(id);
        if (!pos) return true; // new node not saved before
        if (pos.x === 0 && pos.y === 0) return true; // default broken position
        return false;
      });

      if (isBroken) {
        brokenBusbars.push(busbarId);
      }
    });

    // Second pass: apply saved positions ONLY to valid nodes
    savedLayout.forEach(nodeData => {

      const node = this.cy.getElementById(nodeData.id);

      if (node.nonempty()) {

        // Skip structural nodes of broken busbars
        const belongsToBrokenBusbar = brokenBusbars.some(busbarId => {
          const structure = this.structuralMap[busbarId];
          return (
            structure.start === nodeData.id ||
            structure.end === nodeData.id ||
            structure.connectionNodes.includes(nodeData.id)
          );
        });

        if (!belongsToBrokenBusbar) {
          node.position(nodeData.position);
        }
      }
    });

    // Third pass: regenerate broken busbars
    brokenBusbars.forEach(busbarId => {
      this.regenerateSingleBusbar(busbarId);
    });
  }

  private regenerateSingleBusbar(busbarId: string) {

    const structural = this.structuralMap[busbarId];
    if (!structural) return;

    const baseSegmentWidth = 140;
    const padding = 120;
    const centerX = 600;

    // Keep current Y if possible
    const startNode = this.cy.getElementById(structural.start);
    const currentY = startNode.position().y || 150;

    const endNode = this.cy.getElementById(structural.end);
    const connectionNodes = structural.connectionNodes.map(id =>
      this.cy.getElementById(id)
    );

    const totalSegments = connectionNodes.length + 1;
    const busbarLength = totalSegments * baseSegmentWidth + padding;
    const segmentWidth = busbarLength / totalSegments;

    startNode.position({
      x: centerX - busbarLength / 2,
      y: currentY
    });

    connectionNodes.forEach((node, index) => {
      node.position({
        x: centerX - busbarLength / 2 + segmentWidth * (index + 1),
        y: currentY
      });
    });

    endNode.position({
      x: centerX + busbarLength / 2,
      y: currentY
    });
  }

  onClose() {
    this.close.emit();
  }

  ngOnDestroy(): void {
    if (this.cy) {
      this.cy.destroy();
    }
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['templateInstanceId'] && 
        !changes['templateInstanceId'].firstChange) {

      if (this.templateInstanceId != null) {
        this.getEquipmentSchemaById();
      }
    }
  } 

  setLegendData(data: ApiSchematic) {
    const uniqueMap = new Map<string, SchematicLegendItem>();

    data.nodes.forEach(n => {
      // Only filter non-Busbar nodes
      if ((n.type !== 'Busbar' && (!n.symbol || !n.color))) return;

      const key = `${n.type}-${n.symbol ?? ''}-${n.color ?? ''}`;

      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, {
          type: n.type,
          symbol: n.symbol ?? '',
          color: n.color ?? ''
        });
      }
    });

    data.edges?.forEach(e => {
      let type = '';

      if (!e.acLineDirection) {
        type = AcLineLegendType.AcLine;
      } else if (e.acLineDirection === 'Outgoing') {
        type = AcLineLegendType.Outgoing;
      } else if (e.acLineDirection === 'Incoming') {
        type = AcLineLegendType.Incoming;
      }

      if (!type) return;

      const key = type; // no symbol/color → type is enough

      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, {
          type: type,
          symbol: '',   // intentionally empty
          color: ''     // intentionally empty
        });
      }
    });

    const legendList = Array.from(uniqueMap.values());

    setTimeout(() => {
      this.legendDataChange.emit(legendList);
    });
  }
}